import { useQuery } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  AlertTriangle,
  Users,
  FileText,
  Calendar,
  Target,
} from "lucide-react";

interface OnboardingAnalyticsDashboardProps {
  onboardingId: Id<"clientOnboarding">;
}

const COLORS = {
  completed: "#10b981",
  inProgress: "#3b82f6",
  notStarted: "#6b7280",
  overdue: "#ef4444",
  high: "#ef4444",
  medium: "#f59e0b",
  low: "#10b981",
};

export function OnboardingAnalyticsDashboard({ onboardingId }: OnboardingAnalyticsDashboardProps) {
  // Fetch analytics data
  const analytics = useQuery(api.onboardingAnalytics.getOnboardingAnalytics, {
    onboardingId,
  });

  const orgMetrics = useQuery(api.onboardingAnalytics.getOrganizationMetrics);

  const trends = useQuery(api.onboardingAnalytics.getTaskCompletionTrends, {
    onboardingId,
  });

  if (!analytics || !orgMetrics) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="mb-2 h-4 w-3/4 rounded bg-gray-200"></div>
                <div className="h-8 w-1/2 rounded bg-gray-200"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Prepare data for charts
  const taskStatusData = [
    { name: "Completed", value: analytics.taskAnalytics.completed, color: COLORS.completed },
    { name: "In Progress", value: analytics.taskAnalytics.inProgress, color: COLORS.inProgress },
    { name: "Not Started", value: analytics.taskAnalytics.notStarted, color: COLORS.notStarted },
  ];

  const priorityData = Object.entries(analytics.priorityBreakdown).map(([priority, data]) => ({
    priority: priority.charAt(0).toUpperCase() + priority.slice(1),
    total: (data as any).total,
    completed: (data as any).completed,
    completionRate:
      (data as any).total > 0
        ? Math.round(((data as any).completed / (data as any).total) * 100)
        : 0,
  }));

  const documentStatusData = [
    { name: "Draft", value: analytics.documentAnalytics.draft, color: COLORS.notStarted },
    { name: "Pending", value: analytics.documentAnalytics.pending, color: COLORS.medium },
    { name: "Approved", value: analytics.documentAnalytics.approved, color: COLORS.inProgress },
    { name: "Signed", value: analytics.documentAnalytics.signed, color: COLORS.completed },
  ];

  const isAheadOfSchedule =
    analytics.overallProgress > analytics.timelineAnalytics.percentTimeElapsed;
  const isBehindSchedule =
    analytics.overallProgress < analytics.timelineAnalytics.percentTimeElapsed;

  return (
    <div className="space-y-6">
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Overall Progress</p>
                <p className="text-2xl font-bold">{analytics.overallProgress}%</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                <Target className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-4">
              <Progress value={analytics.overallProgress} className="h-2" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Tasks Completed</p>
                <p className="text-2xl font-bold">
                  {analytics.taskAnalytics.completed}/{analytics.taskAnalytics.total}
                </p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
            {analytics.taskAnalytics.overdue > 0 && (
              <div className="mt-2">
                <Badge variant="destructive" className="text-xs">
                  {analytics.taskAnalytics.overdue} overdue
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Days Remaining</p>
                <p className="text-2xl font-bold">
                  {analytics.timelineAnalytics.isOverdue
                    ? `-${analytics.timelineAnalytics.daysOverdue}`
                    : analytics.timelineAnalytics.remainingDays}
                </p>
              </div>
              <div
                className={`flex h-12 w-12 items-center justify-center rounded-full ${
                  analytics.timelineAnalytics.isOverdue ? "bg-red-100" : "bg-orange-100"
                }`}
              >
                <Clock
                  className={`h-6 w-6 ${
                    analytics.timelineAnalytics.isOverdue ? "text-red-600" : "text-orange-600"
                  }`}
                />
              </div>
            </div>
            <div className="mt-2">
              {analytics.timelineAnalytics.isOverdue ? (
                <Badge variant="destructive" className="text-xs">
                  Overdue
                </Badge>
              ) : isAheadOfSchedule ? (
                <Badge className="bg-green-100 text-xs text-green-800">Ahead of schedule</Badge>
              ) : isBehindSchedule ? (
                <Badge className="bg-yellow-100 text-xs text-yellow-800">Behind schedule</Badge>
              ) : (
                <Badge className="bg-blue-100 text-xs text-blue-800">On track</Badge>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Est. Completion</p>
                <p className="text-sm font-bold">{analytics.estimatedCompletionDate || "TBD"}</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-100">
                <Calendar className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <div className="mt-2">
              <p className="text-xs text-gray-500">
                vs. Org Avg: {orgMetrics.averageCompletionDays} days
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Task Status Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              Task Status Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={taskStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {taskStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 flex justify-center gap-4">
              {taskStatusData.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-sm">
                    {item.name}: {item.value}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Priority Completion Rates */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Completion by Priority
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={priorityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="priority" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="completed" fill={COLORS.completed} name="Completed" />
                  <Bar dataKey="total" fill={COLORS.notStarted} name="Total" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Team Performance and Progress Timeline */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Team Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Team Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics.assigneeStats.map((assignee: any, index: any) => (
                <div
                  key={index}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div>
                    <p className="font-medium">{assignee.name}</p>
                    <p className="text-sm text-gray-500">
                      {assignee.completed}/{assignee.total} tasks completed
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <p className="text-sm font-medium">
                        {assignee.total > 0
                          ? Math.round((assignee.completed / assignee.total) * 100)
                          : 0}
                        %
                      </p>
                      {assignee.overdue > 0 && (
                        <Badge variant="destructive" className="text-xs">
                          {assignee.overdue} overdue
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Document Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Document Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={documentStatusData.filter((d) => d.value > 0)}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {documentStatusData
                      .filter((d) => d.value > 0)
                      .map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 flex flex-wrap justify-center gap-4">
              {documentStatusData
                .filter((d) => d.value > 0)
                .map((item) => (
                  <div key={item.name} className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-sm">
                      {item.name}: {item.value}
                    </span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress Timeline */}
      {trends && trends.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Progress Timeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trends.slice(-14)}>
                  {" "}
                  {/* Show last 14 days */}
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(value) => new Date(value).toLocaleDateString()}
                  />
                  <YAxis />
                  <Tooltip labelFormatter={(value) => new Date(value).toLocaleDateString()} />
                  <Line
                    type="monotone"
                    dataKey="progressPercentage"
                    stroke={COLORS.inProgress}
                    strokeWidth={2}
                    name="Progress %"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
